import { CraftProductOptions } from '@snap-types/models/craft/product-options';
import { SelectedProductOption } from '@snap-types/models/snap-product-options';

type BuildFinishedPartIdInput = {
  partId: string;
  craftProductOptions: CraftProductOptions;
  productOptions: SelectedProductOption[];
};

type BuildFinishedPartIdOutput =
  | {
      isSuccess: true;
      finishedPartId: string;
    }
  | {
      isSuccess: false;
      error: string;
    };

/**
 * Builds a finished part id from the Craft provided part options and the selected options,
 * preserving the order of the part options from Craft.
 */

export function buildFinishedPartId({
  partId,
  craftProductOptions,
  productOptions,
}: BuildFinishedPartIdInput): BuildFinishedPartIdOutput {
  let finishedPartId = partId;
  const selectedOptions = Array.from(productOptions);

  for (const partOptionGroup of craftProductOptions.optionGroups) {
    const selectedOption = selectedOptions.find(
      (selectedOption) => selectedOption.groupName === partOptionGroup.name,
    );

    if (!selectedOption) {
      return {
        isSuccess: false,
        error: `Option group "${partOptionGroup.name}" not found`,
      };
    }

    const option = partOptionGroup.options?.find(
      (option) => option.name === selectedOption.optionName,
    );

    if (!option) {
      return {
        isSuccess: false,
        error: `Option "${selectedOption.optionName}" not found in option group "${selectedOption.groupName}"`,
      };
    }

    const code = option.code;

    if (code) {
      finishedPartId = `${finishedPartId}-${code}`;
    }

    if (selectedOption.subOptionName) {
      const suboption = option.subOptions?.find(
        (suboption) => suboption.name === selectedOption.subOptionName,
      );

      if (!suboption) {
        return {
          isSuccess: false,
          error: `Suboption "${selectedOption.subOptionName}" not found in option "${selectedOption.optionName}"`,
        };
      }

      if (suboption.code) {
        finishedPartId = `${finishedPartId}-${suboption.code}`;
      }
    } else if (option.subOptions?.length) {
      return {
        isSuccess: false,
        error: `Option "${selectedOption.optionName}" has suboptions but no suboption was selected`,
      };
    }

    selectedOptions.splice(selectedOptions.indexOf(selectedOption), 1);
  }

  if (selectedOptions.length) {
    return {
      isSuccess: false,
      error: `Unknown options found: ${selectedOptions
        .map((option) => `${option.groupName} - ${option.optionName}`)
        .join(', ')}`,
    };
  }

  return {
    isSuccess: true,
    finishedPartId,
  };
}
